/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Divider, Grid, LinearProgress, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';

import {
  PaymentStatus,
  Status,
  StatusCode,
  convertStatusUrl,
} from '../../../../api/enumerations';
import { changePaymentStatus, getHivePayment } from '../../../../api/financial';
import { HivePaymentData } from '../../../../api/financial/types';
import { Constants } from '../../../../constants/finance';
import {
  IconAttachFileMS,
  IconCloseMS,
  IconDescriptionMS,
  IconDownloadMS,
  IconFolderOpenMS,
  IconPaymentsMS,
} from '../../../../constants/icons';
import { formatOnlyDate, formatReal } from '../../../../helpers';
import useErrorMessage from '../../../../hooks/useErrorMessage';
import useGeneral from '../../../../hooks/useGeneral';
import useSnackbar from '../../../../hooks/useSnackbar';
import CancelDialog from '../CancelDialog';
import { PaymentHeader } from '../PaymentHeader';
import { Reason } from './Reason';
import {
  CloseButton,
  FileContainer,
  FileList,
  FileNameTypography,
  OSLink,
  SectionContainer,
  SectionTitle,
  StyledDialog,
  StyledIcon,
  TopBox,
} from './styles';

export interface PaymentDialogProps {
  open: boolean;
  handleClose: () => void;
  paymentId: number;
  updateData: () => void;
}

export default function PaymentDialog({
  open,
  handleClose,
  paymentId,
  updateData,
}: PaymentDialogProps): JSX.Element {
  const [loadingRefund, setLoadingRefund] = useState(false);
  const [reason, setReason] = useState('');
  const [paymentData, setPaymentData] = useState<HivePaymentData | null>(null);

  const isRefund = paymentData?.status === PaymentStatus.PAID;
  const showReason =
    paymentData?.status === PaymentStatus.REFUNDED ||
    paymentData?.status === PaymentStatus.CANCELLED;

  const { handleDownloadFile } = useGeneral();
  const { getErrorMessage } = useErrorMessage();
  const { handleSnackbar } = useSnackbar();

  const onClose = (): void => {
    handleClose();
    setReason('');
  };

  const getDataCallback = useCallback(async () => {
    try {
      const response = await getHivePayment(paymentId);

      if (response.detail.description)
        throw new Error(response.detail.description);

      if (!response.data) throw new Error('Algo deu errado, tente novamente.');

      setPaymentData(response.data);
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    }
  }, [paymentId]);

  useEffect(() => {
    getDataCallback();
  }, [getDataCallback]);

  const navigateToWorkOrder = (osId: number, osStatus: Status): string => {
    const statusUrl = convertStatusUrl(osStatus);
    return `/home/property/${osId}/${statusUrl}`;
  };

  const handleStatus = async (): Promise<void> => {
    if (!reason) {
      const type = isRefund ? Constants.chargeback : Constants.cancellation;
      handleSnackbar(`Informe o motivo da solicitação do ${type}`, true);
      return;
    }

    try {
      setLoadingRefund(true);

      const response = await changePaymentStatus(paymentId, {
        status: isRefund ? PaymentStatus.REFUNDED : PaymentStatus.CANCELLED,
        canceled_refunded_reason: reason,
      });

      if (response.detail.description)
        throw new Error(response.detail.description);

      if (response.detail.status_code !== StatusCode.OK)
        throw new Error('Algo deu errado, tente novamente.');

      const type = isRefund ? Constants.refunded : Constants.cancelled;
      handleSnackbar(`O pagamento foi ${type} com sucesso!`, false);
      updateData();
      onClose();
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    } finally {
      setLoadingRefund(false);
    }
  };

  return (
    <StyledDialog open={open} onClose={onClose}>
      {paymentData ? (
        <Box>
          <TopBox>
            <CloseButton onClick={onClose}>{IconCloseMS}</CloseButton>
          </TopBox>
          <PaymentHeader paymentData={paymentData} />
          <Reason
            reason={paymentData.canceled_refunded_reason}
            status={paymentData.status}
          />
          <SectionContainer>
            <SectionTitle>
              {IconPaymentsMS}
              {Constants.paymentInfo}
            </SectionTitle>
            <Grid container spacing={2} mb={2}>
              <Grid item xs={4}>
                {Constants.licensed}
                <Typography fontWeight={600}>
                  {paymentData.licensed_company_name}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                {Constants.notice}
                <Typography fontWeight={600}>
                  {paymentData.public_notice_name}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                {Constants.createdAt}
                <Typography fontWeight={600}>
                  {formatOnlyDate(paymentData.created_at)}
                </Typography>
              </Grid>
              {[
                PaymentStatus.APPROVED,
                PaymentStatus.INVOICED,
                PaymentStatus.PAID,
              ].includes(paymentData.status) &&
                paymentData.approved_at && (
                  <Grid item xs={4}>
                    {Constants.approvedAt}
                    <Typography fontWeight={600}>
                      {formatOnlyDate(paymentData.approved_at)}
                    </Typography>
                  </Grid>
                )}
              {[PaymentStatus.INVOICED, PaymentStatus.PAID].includes(
                paymentData.status
              ) &&
                paymentData.invoiced_at && (
                  <Grid item xs={4}>
                    {Constants.invoicedAt}
                    <Typography fontWeight={600}>
                      {formatOnlyDate(paymentData.invoiced_at)}
                    </Typography>
                  </Grid>
                )}
              {paymentData.status === PaymentStatus.PAID &&
                paymentData.paid_at && (
                  <Grid item xs={4}>
                    {Constants.paidAt}
                    <Typography fontWeight={600}>
                      {formatOnlyDate(paymentData.paid_at)}
                    </Typography>
                  </Grid>
                )}
            </Grid>
            {paymentData.files && paymentData.files.length > 0 && (
              <Box>
                <Divider sx={{ mb: 2 }} />
                <SectionTitle>
                  {IconFolderOpenMS}
                  {Constants.files}
                </SectionTitle>
                <FileList>
                  {paymentData.files.map((file) => (
                    <FileContainer container key={file.id}>
                      <Box>
                        <FileNameTypography>
                          {IconAttachFileMS}
                          {file.filename}
                        </FileNameTypography>
                      </Box>
                      <Box>
                        <StyledIcon
                          onClick={() =>
                            file.file &&
                            handleDownloadFile(file.file, file.filename)
                          }
                        >
                          {IconDownloadMS}
                        </StyledIcon>
                      </Box>
                    </FileContainer>
                  ))}
                </FileList>
              </Box>
            )}
          </SectionContainer>
          {paymentData.work_orders && (
            <SectionContainer>
              <SectionTitle>
                {IconDescriptionMS}
                {Constants.associatedWorkOrders}
              </SectionTitle>
              {paymentData.work_orders.length > 0 ? (
                paymentData.work_orders.map((workOrder, i) => (
                  <Box key={workOrder.reference_number}>
                    <OSLink
                      to={navigateToWorkOrder(
                        workOrder.work_order_id,
                        workOrder.work_order_status
                      )}
                      target="blank"
                    >
                      OS-{workOrder.reference_number}
                    </OSLink>
                    <Grid container spacing={2} mb={2} mt={1}>
                      <Grid item xs={3.5}>
                        {Constants.createdAt}
                        <Typography fontWeight={600}>
                          {formatOnlyDate(workOrder.created_at)}
                        </Typography>
                      </Grid>
                      <Grid item xs={5}>
                        {Constants.finishedAt}
                        <Typography fontWeight={600}>
                          {formatOnlyDate(workOrder.hive_finished_at)}
                        </Typography>
                      </Grid>
                      <Grid item xs={3.5}>
                        {Constants.value}
                        <Typography fontWeight={600}>
                          {formatReal(workOrder.price)}
                        </Typography>
                      </Grid>
                    </Grid>
                    {paymentData.work_orders &&
                      paymentData.work_orders.length - 1 !== i && (
                        <Divider sx={{ marginBottom: '24px' }} />
                      )}
                  </Box>
                ))
              ) : (
                <Box>{Constants.noAssociatedWorkOrders}</Box>
              )}
            </SectionContainer>
          )}
          {!showReason && (
            <CancelDialog
              isRefund={isRefund}
              loading={loadingRefund}
              reason={reason}
              setReason={setReason}
              onSubmit={handleStatus}
            />
          )}
        </Box>
      ) : (
        <Box height="400px">
          <LinearProgress />
        </Box>
      )}
    </StyledDialog>
  );
}
